import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { 
  Globe, Users, Building2, Search, Filter, RefreshCw,
  Eye, ToggleLeft, ToggleRight, CheckCircle, Clock, XCircle,
  Mail, Calendar, ArrowRight
} from 'lucide-react';
import Sidebar from '../components/Sidebar';
import FilterSelect from '../components/FilterSelect';
import FilterInput from '../components/FilterInput';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';

const BRAND_BLUE = '#2B4B6A';

const AdminGlobalUsers = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all'); // all, global, regular
  const [togglingUser, setTogglingUser] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  
  const isMainAdmin = user?.role === 'admin' || user?.role === 'main_admin';

  useEffect(() => {
    if (!isMainAdmin) {
      navigate('/admin/dashboard');
      return;
    }
    fetchUsers();
  }, [page, filterType]);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('page', page);
      params.append('limit', 20);
      
      if (filterType === 'global') {
        params.append('is_global_user', 'true');
      } else if (filterType === 'regular') {
        params.append('is_global_user', 'false');
      }

      const res = await api.get(`/admin/users?${params.toString()}`);
      setUsers(res.data.users || res.data || []);
      setTotalPages(res.data.total_pages || 1);
    } catch (error) {
      console.error('Failed to fetch users:', error);
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const toggleGlobalUser = async (userId, currentStatus) => {
    setTogglingUser(userId);
    try {
      await api.put(`/admin/users/${userId}`, {
        is_global_user: !currentStatus
      });
      toast.success(`User ${!currentStatus ? 'enabled' : 'disabled'} as global user`);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update user');
    } finally {
      setTogglingUser(null);
    }
  };

  const filteredUsers = users.filter(u => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      u.email?.toLowerCase().includes(search) ||
      u.first_name?.toLowerCase().includes(search) ||
      u.last_name?.toLowerCase().includes(search) ||
      u.company_name?.toLowerCase().includes(search)
    );
  });

  const globalUsersCount = users.filter(u => u.is_global_user).length;
  const regularUsersCount = users.filter(u => !u.is_global_user).length;

  return (
    <Sidebar>
      <div className="p-6 md:p-8 bg-gray-50/50 min-h-screen">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900 flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: '#10B98115' }}>
                <Globe className="w-5 h-5 text-teal-600" />
              </div>
              Global Users Management
            </h1>
            <p className="text-gray-500 text-sm mt-1">
              Manage users who can access multiple companies
            </p>
          </div>
          
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/admin/access-requests')}
              className="flex items-center gap-2 px-4 py-2.5 text-white rounded-xl text-sm font-medium transition"
              style={{ backgroundColor: BRAND_BLUE }}
              data-testid="view-access-requests"
            >
              <Users className="w-4 h-4" />
              View Access Requests
              <ArrowRight className="w-4 h-4" />
            </button>
            <button
              onClick={fetchUsers}
              className="flex items-center gap-2 px-4 py-2.5 bg-white border border-gray-200 rounded-xl text-sm font-medium text-gray-700 hover:bg-gray-50 transition"
              data-testid="refresh-users"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                <Users className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{users.length}</p>
                <p className="text-xs text-gray-500">Total Users</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-teal-100 flex items-center justify-center">
                <Globe className="w-5 h-5 text-teal-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{globalUsersCount}</p>
                <p className="text-xs text-gray-500">Global Users</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center">
                <Building2 className="w-5 h-5 text-gray-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{regularUsersCount}</p>
                <p className="text-xs text-gray-500">Single-Company Users</p>
              </div>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl border border-gray-200 p-4 mb-6">
          <div className="flex flex-col md:flex-row gap-4">
            {/* Search */}
            <FilterInput
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by name, email, or company..."
              className="flex-1"
              data-testid="search-users"
            />

            {/* Filter */}
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-gray-400" />
              <FilterSelect
                value={filterType}
                onChange={(e) => {
                  setFilterType(e.target.value);
                  setPage(1);
                }}
                options={[
                  { value: 'all', label: 'All Users' },
                  { value: 'global', label: 'Global Users Only' },
                  { value: 'regular', label: 'Single-Company Only' }
                ]}
                data-testid="user-filter"
              />
            </div>
          </div>
        </div>

        {/* Users List */}
        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="animate-spin w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full"></div>
            </div>
          ) : filteredUsers.length === 0 ? (
            <div className="text-center py-20">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Users className="w-8 h-8 text-gray-400" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-1">No users found</h3>
              <p className="text-gray-500 text-sm">Try adjusting your search or filters.</p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="text-left px-6 py-4 text-xs font-medium text-gray-500 uppercase tracking-wider">User</th>
                      <th className="text-left px-6 py-4 text-xs font-medium text-gray-500 uppercase tracking-wider">Home Company</th>
                      <th className="text-center px-6 py-4 text-xs font-medium text-gray-500 uppercase tracking-wider">Global Status</th>
                      <th className="text-center px-6 py-4 text-xs font-medium text-gray-500 uppercase tracking-wider">Role</th>
                      <th className="text-right px-6 py-4 text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {filteredUsers.map((u) => (
                      <tr key={u.id} className="hover:bg-gray-50 transition">
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white font-bold">
                              {u.first_name?.charAt(0)?.toUpperCase() || u.email?.charAt(0)?.toUpperCase() || '?'}
                            </div>
                            <div className="min-w-0">
                              <p className="font-medium text-gray-900 truncate">
                                {u.first_name && u.last_name 
                                  ? `${u.first_name} ${u.last_name}` 
                                  : 'No name'}
                              </p>
                              <p className="text-sm text-gray-500 truncate">{u.email}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          {u.company_name ? (
                            <div className="flex items-center gap-2">
                              <Building2 className="w-4 h-4 text-gray-400" />
                              <span className="text-sm text-gray-700">{u.company_name}</span>
                            </div>
                          ) : (
                            <span className="text-sm text-gray-400">No company</span>
                          )}
                        </td>
                        <td className="px-6 py-4 text-center">
                          {u.is_global_user ? (
                            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-teal-100 text-teal-800">
                              <Globe className="w-3 h-3" /> Global
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                              <Building2 className="w-3 h-3" /> Single
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-4 text-center">
                          <span className={`inline-block px-2.5 py-1 rounded-full text-xs font-medium capitalize ${
                            u.role === 'admin' || u.role === 'main_admin'
                              ? 'bg-purple-100 text-purple-800'
                              : u.role === 'company_admin'
                              ? 'bg-blue-100 text-blue-800'
                              : 'bg-gray-100 text-gray-600'
                          }`}>
                            {u.role || 'user'}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center justify-end gap-2">
                            {/* Don't allow toggling for admins */}
                            {u.role !== 'admin' && u.role !== 'main_admin' && u.role !== 'company_admin' && (
                              <button
                                onClick={() => toggleGlobalUser(u.id, u.is_global_user)}
                                disabled={togglingUser === u.id}
                                className={`p-2 rounded-lg transition ${
                                  u.is_global_user
                                    ? 'text-teal-600 hover:bg-teal-50'
                                    : 'text-gray-400 hover:bg-gray-100'
                                }`}
                                title={u.is_global_user ? 'Disable global access' : 'Enable global access'}
                                data-testid={`toggle-global-${u.id}`}
                              >
                                {togglingUser === u.id ? (
                                  <div className="w-5 h-5 border-2 border-current border-t-transparent rounded-full animate-spin" />
                                ) : u.is_global_user ? (
                                  <ToggleRight className="w-5 h-5" />
                                ) : (
                                  <ToggleLeft className="w-5 h-5" />
                                )}
                              </button>
                            )}
                            <button
                              onClick={() => navigate(`/admin/users/${u.id}`)}
                              className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition"
                              title="View Details"
                              data-testid={`view-user-${u.id}`}
                            >
                              <Eye className="w-5 h-5" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-center gap-2 p-4 border-t border-gray-100">
                  <button
                    onClick={() => setPage(Math.max(1, page - 1))}
                    disabled={page === 1}
                    className="px-3 py-1.5 text-sm rounded-lg border border-gray-200 disabled:opacity-50 hover:bg-gray-50"
                  >
                    Previous
                  </button>
                  <span className="text-sm text-gray-500">
                    Page {page} of {totalPages}
                  </span>
                  <button
                    onClick={() => setPage(Math.min(totalPages, page + 1))}
                    disabled={page === totalPages}
                    className="px-3 py-1.5 text-sm rounded-lg border border-gray-200 disabled:opacity-50 hover:bg-gray-50"
                  >
                    Next
                  </button>
                </div>
              )}
            </>
          )}
        </div>

        {/* Info Box */}
        <div className="mt-6 bg-teal-50 border border-teal-200 rounded-xl p-4">
          <h4 className="font-medium text-teal-800 mb-2 flex items-center gap-2">
            <Globe className="w-4 h-4" />
            About Global Users
          </h4>
          <p className="text-sm text-teal-700">
            Global users can request access to multiple companies without creating separate accounts.
            When enabled, they'll see a company switcher in their dashboard and can submit access 
            requests to other companies. Company admins must approve these requests before access is granted.
          </p>
        </div>
      </div>
    </Sidebar>
  );
};

export default AdminGlobalUsers;
